import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/GridLegacy';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Paper,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  DirectionsCar as VehicleIcon,
  DateRange as CalendarIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  AttachFile as AttachFileIcon,
  OpenInNew as OpenInNewIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import PageLoader from '../components/PageLoader';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
}

const PermitEdit = () => {
  const { t } = useTranslation();
  const { permitId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgContext = useOrg();
  const orgPrefix = `/${(orgContext?.orgCode || localStorage.getItem('orgCode') || 'rta').toLowerCase()}`;
  const defaultAuthority = (orgContext?.orgCode || localStorage.getItem('orgCode') || 'RTA').toUpperCase();

  // Form states
  const [formData, setFormData] = useState({
    authority: defaultAuthority,
    permit_type: '',
    vehicle_type: '',
    vehicle_number: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_capacity: '',
    owner_name: '',
    owner_email: '',
    owner_phone: '',
    owner_address: '',
    owner_cnic: '',
    status: 'pending',
    valid_from: '',
    valid_to: '',
    description: '',
    remarks: '',
    approved_routes: '',
    restrictions: '',
    assignment_details: '',
  });

  const [permit, setPermit] = useState(null);
  const [permitTypes, setPermitTypes] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [basicInfoSubTab, setBasicInfoSubTab] = useState(0);
  const [vehicleDetailsSubTab, setVehicleDetailsSubTab] = useState(0);
  const [ownerDetailsSubTab, setOwnerDetailsSubTab] = useState(0);
  const [validityPeriodSubTab, setValidityPeriodSubTab] = useState(0);
  const [additionalInfoSubTab, setAdditionalInfoSubTab] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [chalans, setChalans] = useState([]);
  const [loadingChalans, setLoadingChalans] = useState(false);
  const [routesList, setRoutesList] = useState([]);

  // Check if user can change status (admins and assistants)
  // Handle case where user.role might be an object {id, name} or a string
  const canChangeStatus = user?.features?.some(f =>
  (f.name?.toLowerCase() === 'permit_change_status' ||
    f.toLowerCase?.() === 'permit_change_status')
  );

  // Check if user is the assigned employee or admin
  const isAssignedEmployee = permit?.assigned_to === user?.id;
  const isAdmin = user?.is_staff;
  const canEditForm = isAdmin || isAssignedEmployee;

  // Debug logging for status field
  useEffect(() => {
    const userRoleName = (user?.role?.name || user?.role || '').toLowerCase().trim();
    console.log('[PermitEdit] Access Control Debug:', {
      username: user?.username,
      userId: user?.id,
      isAdmin: user?.is_staff,
      rawRole: user?.role,
      extractedRoleName: userRoleName,
      canChangeStatus: canChangeStatus,
      assignedToId: permit?.assigned_to,
      isAssignedEmployee: isAssignedEmployee,
      canEditForm: canEditForm
    });
  }, [user, canChangeStatus, permit, isAssignedEmployee, canEditForm]);

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      const [permitRes, typesRes, vehiclesRes, docsRes, routesRes, docTypesRes] = await Promise.all([
        apiClient.get(`/permits/${permitId}/`),
        apiClient.get('/permit-types/'),
        apiClient.get('/vehicle-types/'),
        apiClient.get(`/permit-documents/?permit_id=${permitId}`),
        apiClient.get('/routes/').catch(() => null),
        apiClient.get('/document-types/').catch(() => null),
      ]);

      if (routesRes) setRoutesList(routesRes.data.results || routesRes.data || []);
      if (docTypesRes) setDocumentTypes((docTypesRes.data.results || docTypesRes.data || []).filter(t => t.is_active));
      setPermit(permitRes.data);
      setFormData({
        authority: permitRes.data.authority || defaultAuthority,
        permit_type: permitRes.data.permit_type_id || '',
        vehicle_type: permitRes.data.vehicle_type_id || '',
        vehicle_number: permitRes.data.vehicle_number || '',
        vehicle_make: permitRes.data.vehicle_make || '',
        vehicle_model: permitRes.data.vehicle_model || '',
        vehicle_year: permitRes.data.vehicle_year || '',
        vehicle_capacity: permitRes.data.vehicle_capacity || '',
        owner_name: permitRes.data.owner_name || '',
        owner_email: permitRes.data.owner_email || '',
        owner_phone: permitRes.data.owner_phone || '',
        owner_address: permitRes.data.owner_address || '',
        owner_cnic: permitRes.data.owner_cnic || '',
        status: permitRes.data.status || '',
        assignment_details: permitRes.data.assignment_details || '',
        valid_from: permitRes.data.valid_from || '',
        valid_to: permitRes.data.valid_to || '',
        description: permitRes.data.description || '',
        remarks: permitRes.data.remarks || '',
        approved_routes: permitRes.data.approved_routes || '',
        restrictions: permitRes.data.restrictions || '',
        notes: permitRes.data.notes || '',
        business_name: permitRes.data.business_name || '',
        business_location: permitRes.data.business_location || '',
        plate_number_issued_by: permitRes.data.plate_number_issued_by || '',
      });
      setPermitTypes(typesRes.data.results || typesRes.data);
      setVehicleTypes(vehiclesRes.data.results || vehiclesRes.data);
      setDocuments(docsRes.data.results || docsRes.data);
      setError('');
    } catch (err) {
      setError(t('permitEdit.loadFailed'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [permitId, t]);

  const fetchChalans = useCallback(async () => {
    setLoadingChalans(true);
    try {
      const response = await apiClient.get(`/chalans/?permit=${permitId}`);
      setChalans(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to load chalans:', err);
    } finally {
      setLoadingChalans(false);
    }
  }, [permitId]);

  useEffect(() => {
    fetchAllData();
    fetchChalans();
  }, [fetchAllData, fetchChalans]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      const updateData = {
        authority: formData.authority,
        permit_type_id: formData.permit_type ? parseInt(formData.permit_type) : null,
        vehicle_type_id: formData.vehicle_type ? parseInt(formData.vehicle_type) : null,
        vehicle_number: formData.vehicle_number,
        vehicle_make: formData.vehicle_make,
        vehicle_model: formData.vehicle_model,
        vehicle_year: formData.vehicle_year ? parseInt(formData.vehicle_year) : null,
        vehicle_capacity: formData.vehicle_capacity ? parseInt(formData.vehicle_capacity) : null,
        owner_name: formData.owner_name,
        owner_email: formData.owner_email,
        owner_phone: formData.owner_phone,
        owner_address: formData.owner_address,
        owner_cnic: formData.owner_cnic,
        status: formData.status,
        valid_from: formData.valid_from,
        valid_to: formData.valid_to,
        description: formData.description,
        remarks: formData.remarks,
        approved_routes: formData.approved_routes,
        restrictions: formData.restrictions,
        assignment_details: formData.assignment_details,
        notes: formData.notes,
        business_name: formData.business_name,
        business_location: formData.business_location,
        plate_number_issued_by: formData.plate_number_issued_by,
      };

      await apiClient.patch(`/permits/${permitId}/`, updateData);

      // Upload any pending files
      if (pendingFiles.length > 0) {
        for (const pendingFile of pendingFiles) {
          try {
            const formData = new FormData();
            formData.append('file', pendingFile.file);
            formData.append('permit', permitId);
            formData.append('document_type', pendingFile.document_type || 'other');
            formData.append('description', pendingFile.filename);

            const res = await apiClient.post('/permit-documents/', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });

            // Add uploaded document to documents list
            const newDoc = {
              id: res.data.id,
              name: res.data.filename,
              filename: pendingFile.filename,
              size: res.data.file_size,
              type: res.data.file,
              uploadedAt: new Date().toLocaleString(),
              uploadedBy: res.data.uploaded_by,
            };
            setDocuments(prev => [...prev, newDoc]);
          } catch (fileErr) {
            console.error('File upload error:', fileErr);
            setError(t('permitEdit.uploadFileFailed', { name: pendingFile.file.name }));
          }
        }
        setPendingFiles([]); // Clear pending files after upload
      }

      setSuccess(t('permitEdit.updateSuccess'));
      setTimeout(() => {
        navigate(`${orgPrefix}/permit-details/${permitId}`);
      }, 2000);
    } catch (err) {
      console.error('Save error:', err);
      console.error('Response data:', err.response?.data);
      const errorMsg = err.response?.data?.error ||
        err.response?.data?.detail ||
        (typeof err.response?.data === 'object' ? JSON.stringify(err.response?.data) : err.response?.data) ||
        t('permitEdit.saveFailed');
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`${orgPrefix}/permit-details/${permitId}`);
  };

  const handleFileUpload = (e) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setError(t('permitEdit.fileTooLarge', { name: file.name }));
        return;
      }

      // Allowed file types
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setError(t('permitEdit.fileTypeNotAllowed', { name: file.name }));
        return;
      }

      // Add file to pending uploads list
      const pendingFile = {
        id: `pending_${Date.now()}_${Math.random()}`, // Temporary unique ID
        file: file,
        filename: file.name,
        size: file.size,
        type: file.type,
      };

      setPendingFiles(prev => [...prev, pendingFile]);
      setSuccess(t('permitEdit.fileAddedForUpload', { name: file.name }));
      setError('');
    });

    // Reset file input
    e.target.value = '';
  };

  const handleDeleteDocument = (docId) => {
    // Check if it's a pending file or an uploaded document
    const isPending = docId.toString().startsWith('pending_');

    if (isPending) {
      // Remove from pending files
      setPendingFiles(prev => prev.filter(f => f.id !== docId));
      setSuccess(t('permitEdit.fileRemovedFromQueue'));
    } else {
      // Delete from backend
      apiClient.delete(`/permit-documents/${docId}/`)
        .then(() => {
          setDocuments(prev => prev.filter(doc => doc.id !== docId));
          setSuccess(t('permitEdit.documentDeleted'));
        })
        .catch((err) => {
          console.error('Document delete error:', err);
          const errorMessage = err.response?.data?.detail ||
            err.response?.data?.error ||
            err.message ||
            t('permitEdit.deleteDocGenericError');
          setError(t('permitEdit.deleteDocFailed', { error: errorMessage }));
        });
    }
  };

  const handleDownloadDocument = (doc) => {
    // If the document has a file property (locally uploaded), download from blob
    if (doc.file && doc.file instanceof File) {
      const url = URL.createObjectURL(doc.file);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Otherwise, download from backend API
      apiClient.get(`/permit-documents/${doc.id}/`, {
        responseType: 'blob',
      })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          const link = document.createElement('a');
          link.href = url;
          link.download = doc.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        })
        .catch((err) => {
          console.error('Download error:', err);
          setError(t('permitEdit.downloadFailed', { name: doc.name }));
        });
    }
  };

  if (loading) {
    return <PageLoader variant="form" rows={6} />;
  }

  return (
    <Box sx={{ p: 3, minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          pb: 3,
          borderBottom: '2px solid #e0e0e0',
          background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(21, 101, 192, 0.04) 100%)',
          borderRadius: '8px',
          p: 2.5,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 1,
            fontWeight: 700,
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {t('permitEdit.pageTitle')}
        </Typography>
        <Typography component="div" variant="body2" color="textSecondary" sx={{ fontSize: '1rem' }}>
          {t('permitEdit.permitNumberPrefix')} <Chip
            label={permit?.permit_number}
            variant="outlined"
            size="small"
            sx={{ ml: 1, fontWeight: 600 }}
          />
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '8px' }}>{success}</Alert>}

      {/* Access Control Alert */}
      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
          {!canEditForm ? (
            <>{t('permitEdit.notAssignedNotice')}</>
          ) : (
            <>
              ℹ️ <strong>{t('permitEdit.limitedAccessStrong')}</strong> {t('permitEdit.limitedAccessText')} <strong>{t('permitEdit.cannotChangeStatusStrong')}</strong>{t('permitEdit.limitedAccessSuffix')}
            </>
          )}
        </Alert>
      )}

      {/* Main Edit Form with Tabs */}
      <Card
        sx={{
          mb: 3,
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
        }}
      >
        {/* Tabs Header */}
        <Box sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e3f2fd 100%)', borderBottom: '1px solid #e0e0e0' }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2',
                height: 3,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                color: '#666',
                '&.Mui-selected': {
                  color: '#1976d2',
                  fontWeight: 600,
                },
              },
            }}
          >
            <Tab label={t('permitEdit.basicInfoTab')} icon={<EditIcon sx={{ mr: 1 }} />} iconPosition="start" />
            <Tab label={t('permitEdit.vehicleDetailsTab')} icon={<VehicleIcon sx={{ mr: 1 }} />} iconPosition="start" />
            <Tab label={t('permitEdit.ownerDetailsTab')} icon={<PersonIcon sx={{ mr: 1 }} />} iconPosition="start" />
            <Tab label={t('permitEdit.validityPeriodTab')} icon={<CalendarIcon sx={{ mr: 1 }} />} iconPosition="start" />
            <Tab label={t('permitEdit.additionalInfoTab')} icon={<DescriptionIcon sx={{ mr: 1 }} />} iconPosition="start" />
            <Tab label={t('permitEdit.documentsTab')} icon={<AttachFileIcon sx={{ mr: 1 }} />} iconPosition="start" />
            <Tab label={t('permitEdit.chalansTab', { count: chalans.length })} icon={<ReceiptIcon sx={{ mr: 1 }} />} iconPosition="start" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: 2 }}>
          {/* Tab 1: Basic Information */}
          <TabPanel value={tabValue} index={0}>
            {/* Basic Info Sub-Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={basicInfoSubTab}
                onChange={(e, newValue) => setBasicInfoSubTab(newValue)}
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#1976d2',
                    height: 3,
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#666',
                    '&.Mui-selected': {
                      color: '#1976d2',
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <Tab label={t('permitEdit.permitConfigSubTab')} />
                <Tab label={t('permitEdit.permitVehicleTypesSubTab')} />
              </Tabs>
            </Box>

            {/* Sub-Tab: Permit Configuration */}
            {basicInfoSubTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={true}>
                    <InputLabel>{t('permitEdit.authority')}</InputLabel>
                    <Select
                      name="authority"
                      value={formData.authority}
                      onChange={handleInputChange}
                      label={t('permitEdit.authority')}
                      disabled={true}
                    >
                      <MenuItem value="PTA">{t('permitEdit.provincialAuthority')}</MenuItem>
                      <MenuItem value="RTA">{t('permitEdit.regionalAuthority')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!canChangeStatus}>
                    <InputLabel>{t('permitEdit.status')}</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      label={t('permitEdit.status')}
                      disabled={!canChangeStatus}
                    >
                      <MenuItem value="pending">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ffa726' }} />
                          {t('common.pending')}
                        </Box>
                      </MenuItem>
                      <MenuItem value="active">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#66bb6a' }} />
                          {t('common.active')}
                        </Box>
                      </MenuItem>
                      <MenuItem value="inactive">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ef5350' }} />
                          {t('common.inactive')}
                        </Box>
                      </MenuItem>
                      <MenuItem value="cancelled">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ab47bc' }} />
                          {t('common.cancelled')}
                        </Box>
                      </MenuItem>
                      <MenuItem value="expired">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#9c27b0' }} />
                          {t('common.expired')}
                        </Box>
                      </MenuItem>
                    </Select>
                    {!canChangeStatus && (
                      <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                        {t('permitEdit.statusChangeRestricted')}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {/* Sub-Tab: Permit & Vehicle Types */}
            {basicInfoSubTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('permitEdit.permitType')}</InputLabel>
                    <Select
                      name="permit_type"
                      value={formData.permit_type ? String(formData.permit_type) : ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          permit_type: value === '' ? '' : parseInt(value)
                        }));
                      }}
                      label={t('permitEdit.permitType')}
                    >
                      <MenuItem value="">
                        <em>{t('permitEdit.selectPermitType')}</em>
                      </MenuItem>
                      {permitTypes.map(type => (
                        <MenuItem key={type.id} value={String(type.id)}>
                          {type.name} ({type.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{ minWidth: 200 }}>
                    <InputLabel>{t('permitEdit.vehicleType')}</InputLabel>
                    <Select
                      name="vehicle_type"
                      value={formData.vehicle_type ? String(formData.vehicle_type) : ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          vehicle_type: value === '' ? '' : parseInt(value)
                        }));
                      }}
                      label={t('permitEdit.vehicleType')}
                    >
                      <MenuItem value="">
                        <em>{t('permitEdit.selectVehicleType')}</em>
                      </MenuItem>
                      {vehicleTypes.map(type => (
                        <MenuItem key={type.id} value={String(type.id)}>
                          {type.code ? `${type.name} (${type.code})` : type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Permit Summary Info */}
                {permit && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', borderLeft: '4px solid #1976d2', mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#0d47a1' }}>
                            <strong>{t('permitEdit.permitNumberLabel')}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                            {permit.permit_number}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#0d47a1' }}>
                            <strong>{t('permitEdit.createdDateLabel')}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                            {permit.issued_date ? new Date(permit.issued_date).toLocaleDateString() : t('permitSearch.na')}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </TabPanel>

          {/* Tab 2: Vehicle Details */}
          <TabPanel value={tabValue} index={1}>
            {/* Vehicle Sub-Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={vehicleDetailsSubTab}
                onChange={(e, newValue) => setVehicleDetailsSubTab(newValue)}
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#1976d2',
                    height: 3,
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#666',
                    '&.Mui-selected': {
                      color: '#1976d2',
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <Tab label={t('permitEdit.vehicleIdentificationSubTab')} />
                <Tab label={t('permitEdit.vehicleSpecificationsSubTab')} />
              </Tabs>
            </Box>

            {/* Sub-Tab: Vehicle Identification */}
            {vehicleDetailsSubTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.vehicleNumberLabel')}
                    name="vehicle_number"
                    value={formData.vehicle_number}
                    onChange={handleInputChange}
                    placeholder={t('permitEdit.vehicleNumberPlaceholder')}
                    variant="outlined"
                    inputProps={{ maxLength: 20 }}
                    helperText={t('permitEdit.vehicleNumberHelper')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{ minWidth: 200 }}>
                    <InputLabel>{t('permitEdit.vehicleType')}</InputLabel>
                    <Select
                      name="vehicle_type"
                      value={formData.vehicle_type ? String(formData.vehicle_type) : ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          vehicle_type: value === '' ? '' : parseInt(value)
                        }));
                      }}
                      label={t('permitEdit.vehicleType')}
                    >
                      <MenuItem value="">{t('permitEdit.selectType')}</MenuItem>
                      {vehicleTypes.map(type => (
                        <MenuItem key={type.id} value={String(type.id)}>
                          {type.code ? `${type.name} (${type.code})` : type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {/* Sub-Tab: Vehicle Specifications */}
            {vehicleDetailsSubTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.makeLabel')}
                    name="vehicle_make"
                    value={formData.vehicle_make}
                    onChange={handleInputChange}
                    placeholder={t('permitEdit.makePlaceholder')}
                    variant="outlined"
                    helperText={t('permitEdit.makeHelper')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.modelLabel')}
                    name="vehicle_model"
                    value={formData.vehicle_model}
                    onChange={handleInputChange}
                    placeholder={t('permitEdit.modelPlaceholder')}
                    variant="outlined"
                    helperText={t('permitEdit.modelHelper')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.yearLabel')}
                    name="vehicle_year"
                    type="number"
                    value={formData.vehicle_year}
                    onChange={handleInputChange}
                    inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                    variant="outlined"
                    helperText={t('permitEdit.yearHelper')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.capacityLabel')}
                    name="vehicle_capacity"
                    type="number"
                    value={formData.vehicle_capacity}
                    onChange={handleInputChange}
                    inputProps={{ min: 1, max: 300 }}
                    variant="outlined"
                    helperText={t('permitEdit.capacityHelper')}
                  />
                </Grid>

                {/* Vehicle Summary */}
                {formData.vehicle_number && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2.5, background: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)', borderLeft: '4px solid #4caf50', mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#1b5e20' }}>
                            <strong>{t('permitEdit.vehicleLabel')}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                            {formData.vehicle_make && formData.vehicle_model ? `${formData.vehicle_make} ${formData.vehicle_model}` : formData.vehicle_number}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#1b5e20' }}>
                            <strong>{t('permitEdit.capacityColonLabel')}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                            {formData.vehicle_capacity ? t('permitEdit.seatsUnit', { count: formData.vehicle_capacity }) : t('permitEdit.notSpecified')}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </TabPanel>

          {/* Tab 3: Owner Details */}
          <TabPanel value={tabValue} index={2}>
            {/* Owner Sub-Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={ownerDetailsSubTab}
                onChange={(e, newValue) => setOwnerDetailsSubTab(newValue)}
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#1976d2',
                    height: 3,
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#666',
                    '&.Mui-selected': {
                      color: '#1976d2',
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <Tab label={t('permitEdit.personalDetailsSubTab')} />
                <Tab label={t('permitEdit.contactInfoSubTab')} />
                <Tab label={t('permitEdit.addressSubTab')} />
              </Tabs>
            </Box>

            {/* Sub-Tab: Personal Details */}
            {ownerDetailsSubTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.fullNameLabel')}
                    name="owner_name"
                    value={formData.owner_name}
                    onChange={handleInputChange}
                    placeholder={t('permitEdit.fullNamePlaceholder')}
                    variant="outlined"
                    inputProps={{ maxLength: 100 }}
                    helperText={t('permitEdit.fullNameHelper')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.cnicLabel')}
                    name="owner_cnic"
                    value={formData.owner_cnic}
                    onChange={handleInputChange}
                    placeholder={t('permitEdit.cnicPlaceholder')}
                    variant="outlined"
                    inputProps={{ maxLength: 20 }}
                    helperText={t('permitEdit.cnicHelper')}
                  />
                </Grid>

                {/* Owner Summary - shown on Personal Details */}
                {formData.owner_name && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2.5, background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', borderLeft: '4px solid #ff9800', mt: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#e65100' }}>
                            <strong>{t('permitEdit.ownerLabel')}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f57c00', fontWeight: 600 }}>
                            {formData.owner_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#e65100' }}>
                            <strong>{t('permitEdit.contactLabel')}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f57c00', fontWeight: 600 }}>
                            {formData.owner_phone || formData.owner_email || t('permitEdit.notProvided')}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Sub-Tab: Contact Information */}
            {ownerDetailsSubTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.emailLabel')}
                    name="owner_email"
                    type="email"
                    value={formData.owner_email}
                    onChange={handleInputChange}
                    placeholder={t('permitEdit.emailPlaceholder')}
                    variant="outlined"
                    helperText={t('permitEdit.emailHelper')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.phoneLabel')}
                    name="owner_phone"
                    value={formData.owner_phone}
                    onChange={handleInputChange}
                    placeholder={t('permitEdit.phonePlaceholder')}
                    variant="outlined"
                    inputProps={{ maxLength: 20 }}
                    helperText={t('permitEdit.phoneHelper')}
                  />
                </Grid>
              </Grid>
            )}

            {/* Sub-Tab: Address */}
            {ownerDetailsSubTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.addressLabel')}
                    name="owner_address"
                    multiline
                    rows={3}
                    value={formData.owner_address}
                    onChange={handleInputChange}
                    placeholder={t('permitEdit.addressPlaceholder')}
                    variant="outlined"
                    helperText={t('permitEdit.addressHelper')}
                  />
                </Grid>
              </Grid>
            )}
          </TabPanel>

          {/* Tab 4: Validity Period */}
          <TabPanel value={tabValue} index={3}>
            {/* Validity Period Sub-Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={validityPeriodSubTab}
                onChange={(e, newValue) => setValidityPeriodSubTab(newValue)}
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#1976d2',
                    height: 3,
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#666',
                    '&.Mui-selected': {
                      color: '#1976d2',
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <Tab label={t('permitEdit.validDatesSubTab')} />
                <Tab label={t('permitEdit.durationDetailsSubTab')} />
              </Tabs>
            </Box>

            {/* Sub-Tab: Valid Dates */}
            {validityPeriodSubTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.validFromLabel')}
                    name="valid_from"
                    type="date"
                    value={formData.valid_from}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    helperText={t('permitEdit.validFromHelper')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.validToLabel')}
                    name="valid_to"
                    type="date"
                    value={formData.valid_to}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    helperText={t('permitEdit.validToHelper')}
                  />
                </Grid>
              </Grid>
            )}

            {/* Sub-Tab: Duration Details */}
            {validityPeriodSubTab === 1 && (
              <Grid container spacing={3}>
                {/* Validity Information */}
                {formData.valid_from && formData.valid_to && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2.5, background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', borderLeft: '4px solid #ff9800' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#e65100' }}>
                            <strong>{t('permitEdit.totalDurationLabel')}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f57c00', fontWeight: 600 }}>
                            {t('permitSearch.daysUnit', { count: Math.ceil((new Date(formData.valid_to) - new Date(formData.valid_from)) / (1000 * 60 * 60 * 24)) })}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#e65100' }}>
                            <strong>{t('permitEdit.statusColonLabel')}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{
                            color: '#f57c00',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}>
                            {new Date(formData.valid_to) > new Date() ? (
                              <>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#66bb6a' }} />
                                {t('permitEdit.activeValid')}
                              </>
                            ) : (
                              <>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef5350' }} />
                                {t('common.expired')}
                              </>
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                )}

                {!formData.valid_from || !formData.valid_to ? (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, background: 'linear-gradient(135deg, #f3e5f5 0%, #ede7f6 100%)', borderLeft: '4px solid #9c27b0' }}>
                      <Typography variant="body2" sx={{ color: '#6a1b9a' }}>
                        {t('permitEdit.fillDatesNotice')}
                      </Typography>
                    </Paper>
                  </Grid>
                ) : null}
              </Grid>
            )}
          </TabPanel>

          {/* Tab 5: Additional Information */}
          <TabPanel value={tabValue} index={4}>
            {/* Additional Info Sub-Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={additionalInfoSubTab}
                onChange={(e, newValue) => setAdditionalInfoSubTab(newValue)}
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#1976d2',
                    height: 3,
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#666',
                    '&.Mui-selected': {
                      color: '#1976d2',
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <Tab label={t('permitEdit.generalInfoSubTab')} />
                <Tab label={t('permitEdit.routesRestrictionsSubTab')} />
              </Tabs>
            </Box>

            {/* Sub-Tab: General Information */}
            {additionalInfoSubTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.descriptionLabel')}
                    name="description"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder={t('permitEdit.descriptionPlaceholder')}
                    helperText={t('permitEdit.descriptionHelper')}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.remarksLabel')}
                    name="remarks"
                    multiline
                    rows={3}
                    value={formData.remarks}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder={t('permitEdit.remarksPlaceholder')}
                    helperText={t('permitEdit.remarksHelper')}
                  />
                </Grid>

                {/* Summary Box */}
                {formData.description && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2.5, background: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)', borderLeft: '4px solid #4caf50', mt: 1 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ color: '#1b5e20', fontWeight: 600 }}>
                            <strong>{t('permitEdit.permitSummaryLabel')}</strong>
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ color: '#2e7d32', lineHeight: 1.6 }}>
                            {formData.description.substring(0, 150)}{formData.description.length > 150 ? '...' : ''}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Sub-Tab: Routes & Restrictions */}
            {additionalInfoSubTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>{t('permitEdit.approvedRoutesLabel')}</InputLabel>
                    <Select
                      multiple
                      name="approved_routes"
                      value={formData.approved_routes ? formData.approved_routes.split(', ').filter(Boolean) : []}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFormData(prev => ({
                          ...prev,
                          approved_routes: typeof value === 'string' ? value : value.join(', ')
                        }));
                      }}
                      label={t('permitEdit.approvedRoutesLabel')}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map(val => <Chip key={val} label={val} size="small" />)}
                        </Box>
                      )}
                    >
                      {routesList.map(route => (
                        <MenuItem key={route.id} value={route.name}>
                          {route.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('permitEdit.restrictionsLabel')}
                    name="restrictions"
                    multiline
                    rows={2}
                    value={formData.restrictions}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder={t('permitEdit.restrictionsPlaceholder')}
                    helperText={t('permitEdit.restrictionsHelper')}
                  />
                </Grid>
              </Grid>
            )}
          </TabPanel>

          {/* Tab 6: Documents */}
          <TabPanel value={tabValue} index={5}>
            <Grid container spacing={3}>
              {/* Access Control Alert for Documents Tab */}
              {!canEditForm && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mb: 1, borderRadius: '8px' }}>
                    {t('permitEdit.docsNotAssignedNotice')}
                  </Alert>
                </Grid>
              )}

              {/* Upload Section - per required document type */}
              {canEditForm && (() => {
                const docTypeLabelMap = documentTypes.reduce((acc, dt) => { acc[dt.key] = dt.name; return acc; }, {});
                // Normalise: old format was plain strings, new format is {key, required}
                const normalise = (val) => {
                  if (!Array.isArray(val)) return [];
                  return val.map(item => typeof item === 'string' ? { key: item, required: true } : item);
                };
                const selectedPermitType = permitTypes.find(t => t.id === (permit?.permit_type_id || permit?.permit_type?.id));
                const docTypeEntries = selectedPermitType?.required_document_types?.length
                  ? normalise(selectedPermitType.required_document_types)
                  : documentTypes.map(dt => ({ key: dt.key, required: true }));

                return (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1565c0' }}>
                      {t('permitEdit.uploadRequiredDocs')}
                    </Typography>
                    <Grid container spacing={2}>
                      {docTypeEntries.map(({ key: docType, required: isRequired }) => {
                        const pending = pendingFiles.find(f => f.document_type === docType);
                        const already = documents.find(d => (d.document_type || 'other') === docType);
                        const inputId = `edit-doc-${docType}`;
                        return (
                          <Grid item xs={12} sm={6} key={docType}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                borderColor: pending ? '#f57c00' : already ? '#2e7d32' : '#1976d2',
                                borderStyle: 'dashed',
                                background: pending ? '#fff8e1' : already ? '#f1f8e9' : '#f5f9ff',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: pending ? '#e65100' : already ? '#2e7d32' : '#1565c0', flexGrow: 1 }}>
                                  {pending ? '⏳' : already ? '✅' : '📄'} {docTypeLabelMap[docType] || docType}
                                </Typography>
                                <Chip
                                  label={isRequired ? t('permitEdit.compulsory') : t('permitEdit.optional')}
                                  size="small"
                                  color={isRequired ? 'error' : 'success'}
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              </Box>

                              {pending ? (
                                <Box>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-all', mb: 0.5 }}>{pending.filename}</Typography>
                                  <Typography variant="caption" sx={{ color: '#777' }}>{(pending.size / 1024).toFixed(1)} KB — pending save</Typography>
                                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                    <Button size="small" variant="outlined" color="error" onClick={() => setPendingFiles(prev => prev.filter(f => f.document_type !== docType))}>{t('permitEdit.remove')}</Button>
                                    <Button size="small" variant="outlined" component="label" htmlFor={inputId}>
                                      {t('permitEdit.replace')}
                                      <input type="file" id={inputId} hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                        onChange={(e) => {
                                          const file = e.target.files[0];
                                          if (!file) return;
                                          setPendingFiles(prev => [...prev.filter(f => f.document_type !== docType), { id: `pending_${Date.now()}`, file, filename: file.name, size: file.size, document_type: docType }]);
                                          e.target.value = '';
                                        }}
                                      />
                                    </Button>
                                  </Box>
                                </Box>
                              ) : already ? (
                                <Box>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-all', mb: 0.5 }}>{already.filename || already.name}</Typography>
                                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                    <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => already.file && window.open(already.file, '_blank')}>{t('common.view')}</Button>
                                    <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteDocument(already.id)}>{t('common.delete')}</Button>
                                  </Box>
                                </Box>
                              ) : (
                                <Box>
                                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>{t('permitEdit.maxFileSizeNote')}</Typography>
                                  <Button size="small" variant="contained" component="label" htmlFor={inputId} sx={{ bgcolor: '#1976d2' }}>
                                    {t('permitEdit.chooseFile')}
                                    <input type="file" id={inputId} hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;
                                        if (file.size > 10 * 1024 * 1024) { setError(t('permitEdit.fileExceeds10mb')); return; }
                                        setPendingFiles(prev => [...prev.filter(f => f.document_type !== docType), { id: `pending_${Date.now()}`, file, filename: file.name, size: file.size, document_type: docType }]);
                                        e.target.value = '';
                                      }}
                                    />
                                  </Button>
                                </Box>
                              )}
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                );
              })()}

              {/* Documents List */}
              {documents.length > 0 ? (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2.5, background: 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachFileIcon /> {t('permitEdit.uploadedDocuments', { count: documents.length })}
                    </Typography>
                    <Grid container spacing={2}>
                      {documents.map((doc) => (
                        <Grid item xs={12} sm={6} md={4} key={doc.id}>
                          <Paper
                            sx={{
                              p: 2,
                              background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                              border: '1px solid #e0e0e0',
                              borderRadius: '8px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1.5,
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <AttachFileIcon sx={{ color: '#1976d2', fontSize: 24, mt: 0.5 }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', wordBreak: 'break-word' }}>
                                  {doc.filename || doc.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                                  {t('permitEdit.fileColonLabel', { name: doc.name })}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#999' }}>
                                  {(doc.size / 1024).toFixed(2)} KB
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                                  {doc.uploadedAt}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<OpenInNewIcon />}
                                onClick={() => {
                                  // Open document in new tab
                                  if (doc.file) {
                                    window.open(doc.file, '_blank');
                                  }
                                }}
                                sx={{
                                  flex: 1,
                                  fontSize: '12px',
                                  color: '#2196f3',
                                  borderColor: '#2196f3',
                                  '&:hover': {
                                    backgroundColor: '#e3f2fd',
                                  },
                                }}
                              >
                                {t('common.view')}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadDocument(doc)}
                                sx={{
                                  flex: 1,
                                  fontSize: '12px',
                                  color: '#1976d2',
                                  borderColor: '#1976d2',
                                  '&:hover': {
                                    backgroundColor: '#e3f2fd',
                                  },
                                }}
                              >
                                {t('permitEdit.download')}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDeleteDocument(doc.id)}
                                disabled={!canEditForm}
                                sx={{
                                  flex: 1,
                                  fontSize: '12px',
                                  color: canEditForm ? '#f44336' : '#ccc',
                                  borderColor: canEditForm ? '#f44336' : '#ccc',
                                  '&:hover': {
                                    backgroundColor: canEditForm ? '#ffebee' : 'transparent',
                                  },
                                  '&:disabled': {
                                    cursor: 'not-allowed',
                                    opacity: 0.6,
                                  },
                                }}
                              >
                                {t('common.delete')}
                              </Button>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #f3e5f5 0%, #ede7f6 100%)',
                      borderLeft: '4px solid #9c27b0',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#6a1b9a' }}>
                      <strong>{t('permitEdit.noDocumentsStrong')}</strong> {t('permitEdit.noDocumentsText')}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* Tab 7: Chalans */}
          <TabPanel value={tabValue} index={6}>
            <Grid container spacing={2}>
              {loadingChalans ? (
                <Grid item xs={12} sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress />
                </Grid>
              ) : chalans.length > 0 ? (
                chalans.map((chalan) => (
                  <Grid item xs={12} md={6} key={chalan.id}>
                    <Paper
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderLeft: `4px solid ${chalan.status === 'paid' ? '#4caf50' :
                          chalan.status === 'cancelled' ? '#f44336' :
                            chalan.status === 'issued' ? '#1976d2' : '#ff9800'
                          }`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 3 },
                      }}
                      onClick={() => navigate(`${orgPrefix}/chalans/${chalan.id}`)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {t('permitEdit.chalanNumberLabel', { number: chalan.chalan_number })}
                        </Typography>
                        <Chip
                          label={t(`common.${chalan.status}`, { defaultValue: chalan.status }).toUpperCase()}
                          size="small"
                          color={
                            chalan.status === 'paid' ? 'success' :
                              chalan.status === 'cancelled' ? 'error' :
                                chalan.status === 'issued' ? 'info' : 'warning'
                          }
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">{t('permitEdit.amountLabel', { amount: chalan.total_amount })}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('permitEdit.dateLabel', { date: new Date(chalan.created_at).toLocaleDateString() })}</Typography>
                      {chalan.payment_date && (
                        <Typography variant="body2" color="text.secondary">
                          {t('permitEdit.paidLabel', { date: new Date(chalan.payment_date).toLocaleDateString() })}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Alert severity="info">{t('permitEdit.noChalans')}</Alert>
                </Grid>
              )}
            </Grid>
          </TabPanel>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          mt: 4,
          p: 3,
          backgroundColor: '#f5f7fa',
          borderRadius: '12px',
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={handleCancel}
          disabled={saving}
          sx={{
            borderColor: '#ccc',
            color: '#666',
            '&:hover': {
              borderColor: '#999',
              backgroundColor: '#f9f9f9',
            },
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<SaveIcon />}
          onClick={handleSaveChanges}
          disabled={saving}
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
            paddingX: 4,
            '&:hover': {
              boxShadow: '0 6px 16px rgba(25, 118, 210, 0.6)',
            },
            '&:disabled': {
              boxShadow: 'none',
            },
          }}
        >
          {saving ? t('profile.saving') : t('profile.saveChanges')}
        </Button>
      </Box>

    </Box>
  );
};

export default PermitEdit;
